import useClickOutside from 'lib/hooks/useClickOutside';
import { useState, useRef } from 'react';
import styles from './quarter-picker.module.scss';
import classNames from 'classnames';
import {
  CaretLeft,
  CaretRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from '@carbon/react/icons';

interface QuarterPickerProps {
  selectedQuarter: moment.Moment;
  setSelectedQuarter: (quarter: number, year: number) => void;
  label?: string;
  light?: boolean;
}

// This simulates Carbon components with Carbon styling
const QuarterPicker = (props: QuarterPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedQuarter = props.selectedQuarter.quarter();
  const selectedYear = props.selectedQuarter.year();

  const [viewYear, setViewYear] = useState(selectedYear);

  const startYear = viewYear - 2;
  const dropdownRef = useRef(null);
  const calendarContentRef = useRef(null);

  const quarters = [
    { id: 1, label: 'Q1', months: 'Jan - Mar' },
    { id: 2, label: 'Q2', months: 'Apr - Jun' },
    { id: 3, label: 'Q3', months: 'Jul - Sep' },
    { id: 4, label: 'Q4', months: 'Oct - Dec' },
  ];

  const handleSelection = (year: number, quarter: number) => {
    props.setSelectedQuarter(quarter, year);
    setIsOpen(false);
  };

  useClickOutside({
    refs: [dropdownRef, calendarContentRef],
    onClickOutside: () => {
      setIsOpen(false);
    },
  });

  return (
    <div
      className={classNames(styles.container, {
        [styles.light]: props.light,
      })}
    >
      {props.label && <label className={styles.label}>{props.label}</label>}
      <div
        className={classNames(styles.input, {
          [styles.inputFocused]: isOpen,
        })}
        onClick={() => {
          if (!isOpen) {
            setViewYear(selectedYear);
          }
          setIsOpen(!isOpen);
        }}
        ref={dropdownRef}
      >
        <span
          className={styles.inputText}
        >{`Q${selectedQuarter} ${selectedYear}`}</span>
        <ChevronDown />
      </div>
      {isOpen && (
        <div className={styles.calendar} ref={calendarContentRef}>
          {/* Calendar header with navigation */}
          <div className={styles.header}>
            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear - 5)}
              aria-label="Previous 5 years"
            >
              <CaretLeft />
            </button>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft />
            </button>

            <span className={styles.yearIndicator}>
              {startYear} - {startYear + 4}
            </span>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear + 1)}
              aria-label="Next year"
            >
              <ChevronRight />
            </button>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear + 5)}
              aria-label="Next 5 years"
            >
              <CaretRight />
            </button>
          </div>

          {/* Year picker */}
          <div className={styles.yearGrid}>
            {Array.from({ length: 5 }, (_, i) => startYear + i).map((year) => (
              <button
                key={year}
                className={classNames(styles.yearButton, {
                  [styles.yearButtonSelected]: viewYear === year,
                })}
                onClick={() => setViewYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Quarter picker */}
          <div className={styles.quarterGrid}>
            {quarters.map((quarter) => (
              <button
                key={quarter.id}
                className={classNames(styles.quarterButton, {
                  [styles.quarterButtonSelected]:
                    viewYear === selectedYear && quarter.id === selectedQuarter,
                })}
                onClick={() => handleSelection(viewYear, quarter.id)}
              >
                <span className={styles.quarterLabel}>{quarter.label}</span>
                <span className={styles.quarterMonths}>{quarter.months}</span>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              className={styles.footerButton}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterPicker;
